<template>
  <div>
    <!-- К выплате -->
    <b-overlay :show="isUnpaidLoading" rounded="sm">
      <b-card title="К выплате">
        <div v-if="!unpaidRows.length" class="text-center text-muted py-2">
          Невыплаченных заявок нет
        </div>
        <b-table-simple v-else responsive class="mb-0">
          <b-thead>
            <b-tr>
              <b-th>Редактор</b-th>
              <b-th>Единиц</b-th>
              <b-th>Разбивка</b-th>
              <b-th>Сумма</b-th>
              <b-th class="text-right">Действия</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <template v-for="row in unpaidRows">
              <b-tr :key="`row-${row.author.id}`">
                <b-td>
                  {{ row.author.name }}
                  <b-badge v-if="!row.author.is_active" variant="light-secondary" class="ml-50">
                    Отключён
                  </b-badge>
                </b-td>
                <b-td>{{ row.count }}</b-td>
                <b-td>
                  <div v-for="item in row.breakdown" :key="item.type">
                    {{ item.type_label }}: {{ item.count }} — {{ formatRub(item.amount) }}
                  </div>
                </b-td>
                <b-td><strong>{{ formatRub(row.amount) }}</strong></b-td>
                <b-td class="text-right text-nowrap">
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="mr-50"
                    @click="toggleDetails(row.author.id)"
                  >
                    {{ isExpanded(row.author.id) ? 'Скрыть' : 'Подробнее' }}
                  </b-button>
                  <b-button size="sm" variant="primary" @click="openPayModal(row)">
                    Зафиксировать выплату
                  </b-button>
                </b-td>
              </b-tr>
              <b-tr v-if="isExpanded(row.author.id)" :key="`details-${row.author.id}`">
                <b-td colspan="5">
                  <b-overlay :show="detailsLoading[row.author.id] === true" rounded="sm">
                    <div style="min-height: 40px">
                      <div
                        v-for="submission in (details[row.author.id] || [])"
                        :key="submission.id"
                        class="d-flex justify-content-between align-items-start border-bottom py-1"
                      >
                        <div class="flex-grow-1 pr-1">
                          <submission-preview :submission="submission" />
                        </div>
                        <div class="text-right text-nowrap">
                          <div>{{ formatRub(submission.amount) }}</div>
                          <small class="text-muted">{{ formatDateTime(submission.reviewed_at) }}</small>
                        </div>
                      </div>
                    </div>
                  </b-overlay>
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </b-card>
    </b-overlay>

    <b-alert :show="!!lastPayout" variant="success" dismissible @dismissed="lastPayout = null">
      <div v-if="lastPayout" class="alert-body">
        Выплата зафиксирована: {{ lastPayout.author.name }} — {{ formatRub(lastPayout.amount) }}.
        <b-link
          v-if="lastPayout.planfix_task_url"
          :href="lastPayout.planfix_task_url"
          target="_blank"
          rel="noopener"
        >
          Задача #{{ lastPayout.planfix_task_id }}
        </b-link>
      </div>
    </b-alert>

    <!-- История выплат -->
    <b-overlay :show="isHistoryLoading" rounded="sm">
      <b-card title="История выплат">
        <vue-good-table
          :rows="historyRows"
          :columns="historyColumns"
          :sort-options="{ enabled: false }"
          :pagination-options="{ enabled: false }"
        >
          <template slot="emptystate">
            <div class="text-center text-muted py-2">Выплат пока нет</div>
          </template>
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'created_at'">
              {{ formatDateTime(props.row.created_at) }}
            </span>
            <span v-else-if="props.column.field === 'author'">
              {{ props.row.author ? props.row.author.name : '—' }}
            </span>
            <span v-else-if="props.column.field === 'amount'">
              {{ formatRub(props.row.amount) }}
            </span>
            <span v-else-if="props.column.field === 'planfix_task_id'">
              <b-link
                v-if="props.row.planfix_task_url"
                :href="props.row.planfix_task_url"
                target="_blank"
                rel="noopener"
              >
                #{{ props.row.planfix_task_id }}
              </b-link>
              <span v-else>—</span>
            </span>
            <span v-else-if="props.column.field === 'created_by'">
              {{ props.row.created_by ? props.row.created_by.name : '—' }}
            </span>
            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
          </template>
        </vue-good-table>

        <div v-if="total > perPage" class="d-flex justify-content-center mt-2">
          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            class="mb-0"
          />
        </div>
      </b-card>
    </b-overlay>

    <!-- Подтверждение выплаты -->
    <b-modal
      v-model="isPayModalOpen"
      title="Зафиксировать выплату"
      ok-title="Зафиксировать"
      cancel-title="Отмена"
      :ok-disabled="isPaying"
      :cancel-disabled="isPaying"
      :no-close-on-backdrop="isPaying"
      :no-close-on-esc="isPaying"
      :hide-header-close="isPaying"
      @ok.prevent="confirmPay"
    >
      <p v-if="payTarget" class="mb-0">
        Выплатить {{ payTarget.author.name }} {{ formatRub(payTarget.amount) }}
        за {{ payTarget.count }} ед.?
        В Планфиксе будет создана задача «Оплата сотрудникам».
      </p>
      <template #modal-ok>
        <b-spinner v-if="isPaying" small class="mr-50" />
        Зафиксировать
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BOverlay, BLink, BBadge, BButton, BAlert, BPagination, BModal, BSpinner,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTime } from '@core/utils/filter'
import { formatRub } from '@/views/finance/utils'
import SubmissionPreview from './SubmissionPreview.vue'

export default {
  components: {
    BCard,
    BOverlay,
    BLink,
    BBadge,
    BButton,
    BAlert,
    BPagination,
    BModal,
    BSpinner,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    VueGoodTable,
    SubmissionPreview,
  },
  setup() {
    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const errorMessage = err => {
      const data = (err.response && err.response.data) || {}
      if (data.errors) return Object.values(data.errors)[0][0]
      return data.message || 'Ошибка'
    }

    // --- К выплате ---
    const isUnpaidLoading = ref(false)
    const unpaidRows = ref([])
    const expandedAuthorIds = ref([])
    const details = ref({})
    const detailsLoading = ref({})

    const fetchUnpaid = () => {
      isUnpaidLoading.value = true
      return store.dispatch('moderation/fetchUnpaid')
        .then(response => {
          const body = response.data
          unpaidRows.value = Array.isArray(body) ? body : ((body && body.data) || [])
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => { isUnpaidLoading.value = false })
    }

    const isExpanded = authorId => expandedAuthorIds.value.includes(authorId)

    const loadDetails = authorId => {
      detailsLoading.value = { ...detailsLoading.value, [authorId]: true }
      return store.dispatch('moderation/fetchUnpaidForAuthor', authorId)
        .then(response => {
          const body = response.data || {}
          details.value = { ...details.value, [authorId]: body.data || [] }
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => {
          detailsLoading.value = { ...detailsLoading.value, [authorId]: false }
        })
    }

    const toggleDetails = authorId => {
      if (isExpanded(authorId)) {
        expandedAuthorIds.value = expandedAuthorIds.value.filter(id => id !== authorId)
        return
      }
      expandedAuthorIds.value = [...expandedAuthorIds.value, authorId]
      loadDetails(authorId)
    }

    // --- История выплат ---
    const isHistoryLoading = ref(false)
    const historyRows = ref([])
    const total = ref(0)
    const perPage = ref(15)
    const currentPage = ref(1)

    const historyColumns = [
      { label: 'Дата', field: 'created_at' },
      { label: 'Редактор', field: 'author' },
      { label: 'Сумма', field: 'amount' },
      { label: 'Единиц', field: 'submissions_count' },
      { label: 'Задача ПФ', field: 'planfix_task_id' },
      { label: 'Кто зафиксировал', field: 'created_by' },
    ]

    const fetchHistory = () => {
      isHistoryLoading.value = true
      return store.dispatch('moderation/fetchPayouts', { page: currentPage.value })
        .then(response => {
          const body = response.data || {}
          historyRows.value = body.data || []
          const meta = body.meta || {}
          total.value = meta.total || 0
          perPage.value = meta.per_page || 15
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => { isHistoryLoading.value = false })
    }

    watch(currentPage, fetchHistory)

    // --- Подтверждение выплаты ---
    const isPayModalOpen = ref(false)
    const isPaying = ref(false)
    const payTarget = ref(null)
    const lastPayout = ref(null)

    const openPayModal = row => {
      payTarget.value = row
      isPayModalOpen.value = true
    }

    const confirmPay = () => {
      if (isPaying.value || !payTarget.value) return
      const target = payTarget.value
      isPaying.value = true
      store.dispatch('moderation/pay', target.author.id)
        .then(response => {
          const payout = (response.data && response.data.data) || null
          lastPayout.value = payout
          isPayModalOpen.value = false
          payTarget.value = null
          showSuccess(
            payout && payout.planfix_task_id
              ? `Выплата зафиксирована, задача #${payout.planfix_task_id}`
              : 'Выплата зафиксирована',
          )
          // строка автора исчезает, история перезагружается
          expandedAuthorIds.value = expandedAuthorIds.value.filter(id => id !== target.author.id)
          fetchUnpaid()
          if (currentPage.value === 1) fetchHistory()
          else currentPage.value = 1
        })
        .catch(err => {
          showError(errorMessage(err))
          // при 422 («нечего платить») данные устарели — обновляем список
          if (err.response && err.response.status === 422) {
            isPayModalOpen.value = false
            fetchUnpaid()
          }
        })
        .finally(() => { isPaying.value = false })
    }

    fetchUnpaid()
    fetchHistory()

    return {
      isUnpaidLoading,
      unpaidRows,
      details,
      detailsLoading,
      isExpanded,
      toggleDetails,
      isHistoryLoading,
      historyRows,
      historyColumns,
      total,
      perPage,
      currentPage,
      isPayModalOpen,
      isPaying,
      payTarget,
      lastPayout,
      openPayModal,
      confirmPay,
      formatRub,
      formatDateTime,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
